// Columna derecha del explorer (`.col-detail` del mockup): hero + tres pestañas —
// Resumen · Bitácora · Referencias.
//
// Los botones de decisión solo aparecen si el estado los admite. Una excepción APLICADA ya tocó
// datos reales y "des-aprobarla" no revierte nada; una RECHAZADA se vuelve a levantar como caso
// nuevo, para que el rastro quede completo en vez de sobrescrito — eso es «Duplicar».
import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, Check, CheckCheck, Copy, Eye, MessageSquarePlus, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { ErrorState, Skeleton, Tabs } from '@/kit';
import { MasterDetailEmpty } from '@/shared/patterns/MasterDetail';
import { cn } from '@/lib/cn';
import { daysBetweenMx, formatDateMx, formatDateTimeMx } from '@/lib/dateHelpers';
import type { ExceptionDetail } from '@/api/services/retail-wizard/backoffice';
import {
    canApply,
    canResolve,
    canReview,
    formatImpact,
    isClosed,
    isUnknownStatus,
    requesterOf,
    statusBadge,
    timelineBadge,
    typeBadge,
    typeLongLabel,
} from '../lib/excepcionesFormat';
import { MIN_NOTE } from '../lib/excepcionesSchema';

export const DETAIL_TABS = ['resumen', 'bitacora', 'referencias'] as const;
export type DetailTab = (typeof DETAIL_TABS)[number];

const TAB_ITEMS = [
    { key: 'resumen', label: 'Resumen' },
    { key: 'bitacora', label: 'Bitácora' },
    { key: 'referencias', label: 'Referencias' },
] as const;

/**
 * Normaliza el `?tab=` de la URL. La ruta lo valida como `string` porque cada sección del portal
 * tiene sus propias pestañas; reconocer el valor es de cada pantalla. Uno que no conocemos cae en
 * «resumen» en vez de dejar el panel en blanco.
 */
export function parseDetailTab(raw: string | undefined): DetailTab {
    return DETAIL_TABS.includes(raw as DetailTab) ? (raw as DetailTab) : 'resumen';
}

const SECTION =
    'mb-1 mt-4 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground';
const ACTION_BTN =
    'inline-flex h-[28px] items-center gap-1.5 rounded-md border px-3 text-[11.5px] font-medium transition-colors disabled:opacity-50';

/** Quién firmó, con el nombre si el silo lo guardó y el id si no. Nunca un hueco. */
function actorOf(name: string | null, id: string | null): string {
    if (name) return name;
    if (id) return id;
    return 'Autor no registrado';
}

function InfoRow({ label, value }: { label: string; value: ReactNode }) {
    return (
        <div className="grid grid-cols-[104px_1fr] gap-1.5 py-[3px] text-[11.5px]">
            <span className="text-[10px] uppercase tracking-[0.04em] text-muted-foreground">
                {label}
            </span>
            <span className="min-w-0 break-words">{value}</span>
        </div>
    );
}

function KpiCard({ label, value, sub }: { label: string; value: ReactNode; sub: ReactNode }) {
    return (
        <div className="rounded-md border border-border bg-card px-2.5 py-2">
            <div className="text-[9.5px] font-bold uppercase tracking-[0.04em] text-muted-foreground">
                {label}
            </div>
            <div className="mt-0.5 text-[14px] font-bold tabular-nums">{value}</div>
            <div className="mt-px text-[10px] text-muted-foreground">{sub}</div>
        </div>
    );
}

/** Una entrada de la pestaña Referencias: rótulo + enlace a la pantalla dueña del dato. */
function RefRow({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="text-[11.5px]">
            <span className="text-muted-foreground">{label}:</span> {children}
        </div>
    );
}

const REF_LINK = 'text-primary underline-offset-2 hover:underline';

export function ExcepcionDetailPanel({
    exception,
    isLoading,
    error,
    onRetry,
    hasSelection,
    storeName,
    requesterName,
    tab,
    onTabChange,
    onApprove,
    onReject,
    onReview,
    onApply,
    onDuplicate,
    onAddNote,
    isBusy,
}: {
    exception: ExceptionDetail | undefined;
    isLoading: boolean;
    error: Error | null;
    onRetry: () => void;
    hasSelection: boolean;
    storeName: string | null;
    /** Nombre del solicitante resuelto contra el padrón de `retail-org`; `null` si no se pudo. */
    requesterName: string | null;
    tab: DetailTab;
    onTabChange: (tab: DetailTab) => void;
    onApprove: () => void;
    onReject: () => void;
    /** Tomar el caso (`NEW → IN_REVIEW`). */
    onReview: () => void;
    /** Marcar como ejecutada (`APPROVED → APPLIED`). */
    onApply: () => void;
    /** Levantar un caso NUEVO con los datos de este pre-llenados. */
    onDuplicate: () => void;
    onAddNote: (body: string) => void;
    isBusy: boolean;
}) {
    // 🔴 EL BORRADOR SE RESETEA AL CAMBIAR DE CASO — antes el comentario lo afirmaba y el código no
    // lo hacía. Sin esto, escribir en la excepción A, cambiar a la B y darle «Agregar» manda el
    // texto de A a la bitácora de B: queda firmado con tu nombre en el expediente equivocado, en un
    // registro auditable. Reproducido contra la red antes de arreglarlo.
    //
    // El reset lo da el `key={exc}` del padre, que remonta este componente al cambiar de caso: el
    // estado nace limpio por construcción.
    const [note, setNote] = useState('');

    // 🔴 GUARD SÍNCRONO CONTRA EL DOBLE CLICK. `disabled={isBusy}` no alcanza: los dos clicks de un
    // doble click salen antes de que React re-renderice, así que los dos POST llegan al silo. Y el
    // guard de estado del backend NO es atómico — los dos `review` devuelven 200 y dejan dos
    // eventos en el audit trail, uno describiendo una transición que nunca ocurrió.
    const firing = useRef(false);
    useEffect(() => {
        if (!isBusy) firing.current = false;
    }, [isBusy]);

    const once = (fn: () => void) => () => {
        if (firing.current || isBusy) return;
        firing.current = true;
        fn();
    };

    if (!hasSelection) {
        return (
            <MasterDetailEmpty
                icon={<AlertTriangle className="size-8" aria-hidden />}
                title="Selecciona una excepción"
                sub="Elige un caso para leer su justificación, su bitácora y decidir."
            />
        );
    }
    if (error) {
        return (
            <ErrorState
                className="m-3"
                title="No se pudo cargar la excepción"
                description={error.message}
                onRetry={onRetry}
            />
        );
    }
    if (isLoading || !exception) {
        return (
            <div className="flex flex-col gap-3 p-3.5">
                <Skeleton className="h-5 w-56" />
                <Skeleton lines={8} />
            </div>
        );
    }

    const type = typeBadge(exception.type);
    const status = statusBadge(exception.status);
    const closed = isClosed(exception.status);
    const showLifecycleActions =
        canReview(exception.status) || canResolve(exception.status) || canApply(exception.status);
    // `timeline` puede faltar si el silo cambia: se cae a un arreglo vacío en vez de reventar la
    // pantalla entera. Ese `.map()` sobre `undefined` fue el crash que la dejó inusable.
    const timeline = exception.timeline;
    const requester = requesterOf(exception, () => requesterName);
    const storeLabel = storeName ?? exception.storeId ?? 'Sin tienda';

    /**
     * Días abierta. Un caso cerrado NO sigue envejeciendo: se mide contra su resolución.
     *
     * El mockup cuenta siempre contra HOY, y con eso una excepción aplicada hace tres meses decía
     * «92 d abierta» — un número que se lee como deuda operativa cuando el caso ya está cerrado.
     */
    const daysOpen =
        closed && exception.resolvedAt !== null
            ? daysBetweenMx(exception.requestedAt, exception.resolvedAt)
            : daysBetweenMx(exception.requestedAt, new Date());
    const daysSub =
        closed && exception.resolvedAt !== null
            ? `hasta ${formatDateMx(exception.resolvedAt)}`
            : `desde ${formatDateMx(exception.requestedAt)}`;

    const payload = exception.payload ?? {};
    const hasPayload =
        payload.sku !== undefined ||
        payload.amountCents !== undefined ||
        payload.soldAt !== undefined ||
        payload.imei !== undefined;

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            {/* .detail-hero */}
            <div className="flex-none border-b border-border px-3.5 py-3">
                <div className="mb-1 flex items-start justify-between gap-2">
                    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                        <span className="text-[9.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Excepción
                        </span>
                        <span className="truncate rounded-full bg-muted px-1.5 font-mono text-[9.5px] font-semibold text-muted-foreground">
                            {/* eslint-disable-next-line no-restricted-syntax -- el identificador del caso, el que se cita en la bitácora de auditoría */}
                            {exception.exceptionId}
                        </span>
                        <span
                            className={cn(
                                'rounded-full border px-1.5 py-px text-[9.5px] font-semibold',
                                status.className,
                            )}
                        >
                            {status.label}
                        </span>
                    </div>

                    {/* .detail-hero-actions — la barra vive en el hero, como el mockup */}
                    <div className="flex flex-none flex-wrap justify-end gap-1.5">
                        {canReview(exception.status) ? (
                            <button
                                type="button"
                                onClick={once(onReview)}
                                disabled={isBusy}
                                title="Marca que alguien se hizo cargo, para que dos Admin VL no trabajen el mismo caso."
                                className={cn(
                                    ACTION_BTN,
                                    'border-warning/60 text-warning hover:bg-warning/10',
                                )}
                            >
                                <Eye className="size-3.5" aria-hidden />
                                Revisar
                            </button>
                        ) : null}
                        {canResolve(exception.status) ? (
                            <>
                                <button
                                    type="button"
                                    onClick={onApprove}
                                    disabled={isBusy}
                                    className={cn(
                                        ACTION_BTN,
                                        'border-success/60 text-success hover:bg-success/10',
                                    )}
                                >
                                    <Check className="size-3.5" aria-hidden />
                                    Aprobar
                                </button>
                                <button
                                    type="button"
                                    onClick={onReject}
                                    disabled={isBusy}
                                    className={cn(
                                        ACTION_BTN,
                                        'border-destructive/60 text-destructive hover:bg-destructive/10',
                                    )}
                                >
                                    <X className="size-3.5" aria-hidden />
                                    Rechazar
                                </button>
                            </>
                        ) : null}
                        {canApply(exception.status) ? (
                            <button
                                type="button"
                                onClick={once(onApply)}
                                disabled={isBusy}
                                className={cn(
                                    ACTION_BTN,
                                    'border-info/60 text-info hover:bg-info/10',
                                )}
                                // El circuito se cierra por fuera: esto solo deja constancia de que
                                // ya se hizo, no ejecuta el ajuste.
                                title="Marca que el ajuste ya se ejecutó por fuera. No modifica la venta."
                            >
                                <CheckCheck className="size-3.5" aria-hidden />
                                Marcar aplicada
                            </button>
                        ) : null}
                        {/* `.actionsForEstado()` del mockup: en los estados cerrados la única acción
                            es duplicar. Una rechazada no se re-abre — se levanta otra con lo que
                            faltaba, y así el rastro queda completo en vez de sobrescrito. */}
                        {!showLifecycleActions ? (
                            <button
                                type="button"
                                onClick={onDuplicate}
                                title="Levanta una excepción nueva del mismo tipo, con los datos de esta pre-llenados."
                                className={cn(ACTION_BTN, 'border-border hover:bg-muted')}
                            >
                                <Copy className="size-3.5" aria-hidden />
                                Duplicar
                            </button>
                        ) : null}
                    </div>
                </div>

                {/* El silo no manda un título: el encabezado es la primera línea de la
                    justificación, que sí la escribió una persona. */}
                <div className="text-[14px] font-semibold leading-tight">
                    {exception.justification.split('\n', 1)[0].trim()}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <span
                        className={cn(
                            'rounded px-1.5 py-px text-[9.5px] font-bold',
                            type.className,
                        )}
                    >
                        {typeLongLabel(exception.type)}
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                        {exception.saleId ? `${exception.saleId} · ` : ''}
                        {requester} · {storeLabel}
                    </span>
                </div>
                <div className="mt-2 text-[11.5px] text-muted-foreground">
                    {daysOpen} d {closed ? 'hasta cerrarse' : 'abierta'} · impacto{' '}
                    <span
                        className={cn(
                            exception.impactCents !== null &&
                                exception.impactCents < 0 &&
                                'text-success',
                        )}
                    >
                        {formatImpact(exception.impactCents)}
                    </span>
                </div>
            </div>

            {isUnknownStatus(exception.status) ? (
                <div className="flex-none border-b border-border bg-warning-soft px-3.5 py-2 text-[11px] text-warning">
                    Este caso tiene un estado que la aplicación no reconoce
                    {` («${exception.status}»)`}. No se puede operar desde aquí: repórtalo para que
                    lo revisen en el sistema.
                </div>
            ) : null}

            {/* Sin esto, en APPLIED y REJECTED la ficha no decía por qué no hay decisiones que
                tomar. Un caso cerrado tiene que DECIR que está cerrado. */}
            {closed ? (
                <div className="flex-none border-b border-border px-3.5 py-2 text-[11px] text-muted-foreground">
                    Caso cerrado
                    {exception.resolvedAt ? ` el ${formatDateTimeMx(exception.resolvedAt)}` : ''}
                    {` por ${actorOf(exception.resolvedByName, exception.resolvedBy)}`}. Ya no
                    admite decisiones; solo puedes agregar notas o duplicarlo.
                </div>
            ) : null}

            {/* .detail-tabs */}
            <Tabs
                tabs={TAB_ITEMS}
                activeTab={tab}
                onChange={(key) => {
                    onTabChange(parseDetailTab(key));
                }}
                className="flex-none px-2"
                wrap
            />

            <div className="min-h-0 flex-1 overflow-auto px-3.5 py-3">
                {tab === 'resumen' ? (
                    <>
                        <div className="grid grid-cols-2 gap-2">
                            <KpiCard
                                label="Impacto financiero"
                                value={formatImpact(exception.impactCents)}
                                sub={
                                    exception.impactCents === null
                                        ? 'no se capturó un monto'
                                        : exception.impactCents < 0
                                          ? 'descuento o costo a la cadena'
                                          : 'valor involucrado en la venta'
                                }
                            />
                            <KpiCard
                                label={closed ? 'Días hasta cerrarse' : 'Días abierta'}
                                value={daysOpen}
                                sub={daysSub}
                            />
                        </div>

                        <div className={SECTION}>Descripción</div>
                        <p className="whitespace-pre-line text-[11.5px] leading-relaxed">
                            {exception.justification}
                        </p>

                        <div className={SECTION}>Identificación</div>
                        <InfoRow
                            label="ID"
                            value={
                                <span className="font-mono">
                                    {/* eslint-disable-next-line no-restricted-syntax -- el identificador del caso */}
                                    {exception.exceptionId}
                                </span>
                            }
                        />
                        <InfoRow label="Tipo" value={typeLongLabel(exception.type)} />
                        <InfoRow label="Estado" value={status.label} />
                        <InfoRow
                            label="Venta"
                            value={
                                exception.saleId ? (
                                    <span className="font-mono">
                                        {/* eslint-disable-next-line no-restricted-syntax -- el folio de la venta afectada; es el dato por el que se cruza con la pantalla de Ventas */}
                                        {exception.saleId}
                                    </span>
                                ) : (
                                    <span className="text-muted-foreground">Sin venta ligada</span>
                                )
                            }
                        />
                        <InfoRow label="Solicitante" value={requester} />
                        <InfoRow label="Tienda" value={storeLabel} />
                        <InfoRow
                            label="Solicitada"
                            value={formatDateTimeMx(exception.requestedAt)}
                        />
                        <InfoRow
                            label="Aprobador"
                            value={
                                exception.resolvedBy === null ? (
                                    <span className="text-muted-foreground">Sin resolver</span>
                                ) : (
                                    `${actorOf(exception.resolvedByName, exception.resolvedBy)}${
                                        exception.resolvedAt
                                            ? ` · ${formatDateTimeMx(exception.resolvedAt)}`
                                            : ''
                                    }`
                                )
                            }
                        />

                        <div className={SECTION}>Acción propuesta</div>
                        <p className="whitespace-pre-line text-[11.5px] leading-relaxed">
                            {exception.requestedAction ?? (
                                <span className="text-muted-foreground">
                                    No se detalló una acción.
                                </span>
                            )}
                        </p>

                        {/* 🆕 Nuestro: el mockup no lo tiene porque su dato de muestra no lo tiene.
                            El silo manda `payload` tipado en las 229 filas y es EL CAMBIO CONCRETO
                            que se está autorizando — sin esto se aprueba leyendo prosa. */}
                        {hasPayload ? (
                            <>
                                <div className={SECTION}>Cambio solicitado</div>
                                {payload.sku !== undefined ? (
                                    <InfoRow
                                        label="SKU"
                                        value={<span className="font-mono">{payload.sku}</span>}
                                    />
                                ) : null}
                                {payload.amountCents !== undefined ? (
                                    <InfoRow
                                        label="Monto"
                                        value={formatImpact(payload.amountCents)}
                                    />
                                ) : null}
                                {payload.soldAt !== undefined ? (
                                    <InfoRow
                                        label="Fecha de venta"
                                        value={formatDateTimeMx(payload.soldAt)}
                                    />
                                ) : null}
                                {payload.imei !== undefined ? (
                                    <InfoRow
                                        label="IMEI"
                                        value={<span className="font-mono">{payload.imei}</span>}
                                    />
                                ) : null}
                            </>
                        ) : null}

                        {/* 🔴 El silo manda los tres campos PLANOS. El front esperaba un objeto
                            `decision` que nunca llega, así que este bloque no se pintaba ni en los
                            casos resueltos: la resolución existía en la base y era invisible. */}
                        {exception.resolution ? (
                            <>
                                <div className={SECTION}>Resolución</div>
                                <p className="whitespace-pre-line text-[11.5px] leading-relaxed">
                                    {exception.resolution}
                                </p>
                                <p className="mt-1 text-[10px] text-muted-foreground">
                                    {actorOf(exception.resolvedByName, exception.resolvedBy)}
                                    {exception.resolvedAt
                                        ? ` · ${formatDateTimeMx(exception.resolvedAt)}`
                                        : ''}
                                </p>
                            </>
                        ) : null}

                        {/* .info-strip del mockup */}
                        <p className="mt-4 rounded-md border border-border bg-muted/40 p-2.5 text-[11px] leading-snug text-muted-foreground">
                            <span className="font-semibold text-foreground">Sugerencia:</span>{' '}
                            verifica la evidencia con quien la solicitó antes de aprobar. La
                            resolución queda en la bitácora de auditoría con tu nombre.
                        </p>
                    </>
                ) : null}

                {tab === 'bitacora' ? (
                    <>
                        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Historia de la excepción
                        </div>
                        <div className="flex flex-col gap-2">
                            {timeline.map((entry, i) => {
                                const badge = timelineBadge(entry.kind);
                                return (
                                    <div
                                        key={`${entry.at}-${String(i)}`}
                                        className="border-l-2 border-border pl-2.5"
                                    >
                                        <span
                                            className={cn(
                                                'mb-0.5 inline-block rounded-full border px-1.5 py-px text-[9.5px] font-semibold',
                                                badge.className,
                                            )}
                                        >
                                            {badge.label}
                                        </span>
                                        <div className="text-[11px] leading-snug">{entry.body}</div>
                                        <div className="text-[9.5px] text-muted-foreground">
                                            {actorOf(entry.authorName, entry.authorId)} ·{' '}
                                            {formatDateTimeMx(entry.at)}
                                        </div>
                                    </div>
                                );
                            })}
                            {timeline.length === 0 ? (
                                <p className="py-4 text-center text-[11.5px] text-muted-foreground">
                                    Sin eventos registrados.
                                </p>
                            ) : null}
                        </div>

                        {/* La nota se acepta en CUALQUIER estado, incluso con el caso ya cerrado:
                            cerrar la excepción no cierra la conversación sobre ella. */}
                        <form
                            className="mt-3 flex items-start gap-2"
                            onSubmit={(e) => {
                                e.preventDefault();
                                const body = note.trim();
                                // El mínimo espeja el del silo (3): antes bastaba con 1 carácter y
                                // el backend devolvía un 400 crudo, ilegible para el operador.
                                if (body.length < MIN_NOTE || firing.current || isBusy) return;
                                firing.current = true;
                                onAddNote(body);
                                setNote('');
                            }}
                        >
                            <textarea
                                value={note}
                                onChange={(e) => {
                                    setNote(e.target.value);
                                }}
                                rows={2}
                                placeholder="Agrega una nota de seguimiento…"
                                aria-label="Nota de seguimiento"
                                className="min-w-0 flex-1 resize-y rounded-md border border-border bg-background px-2 py-1.5 text-[11.5px] outline-none focus:border-primary"
                            />
                            <button
                                type="submit"
                                disabled={isBusy || note.trim().length < MIN_NOTE}
                                className={cn(ACTION_BTN, 'flex-none border-border hover:bg-muted')}
                            >
                                <MessageSquarePlus className="size-3.5" aria-hidden />
                                Agregar
                            </button>
                        </form>
                    </>
                ) : null}

                {tab === 'referencias' ? (
                    <>
                        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Enlaces
                        </div>
                        <div className="flex flex-col gap-2">
                            {exception.saleId ? (
                                <RefRow label="Venta">
                                    <Link
                                        to="/retail-admin/$section"
                                        params={{ section: 'ventas' }}
                                        search={{ venta: exception.saleId }}
                                        className={cn(REF_LINK, 'font-mono')}
                                    >
                                        {/* eslint-disable-next-line no-restricted-syntax -- el folio de la venta afectada, que es justamente el destino del enlace */}
                                        {exception.saleId}
                                    </Link>
                                </RefRow>
                            ) : null}

                            {/* El enlace al vendedor SOLO cuando el solicitante se resolvió contra
                                el padrón: `requestedBy` es el actor del token, que en las 229 de
                                dev es un `HARNESS-*` sin ficha. Un enlace que aterriza en un 404 es
                                peor que no ofrecerlo. */}
                            {requesterName !== null ? (
                                <RefRow label="Solicitante">
                                    <Link
                                        to="/retail-admin/$section"
                                        params={{ section: 'vendedores' }}
                                        search={{ vendedor: exception.requestedBy ?? undefined }}
                                        className={REF_LINK}
                                    >
                                        {requesterName}
                                    </Link>
                                </RefRow>
                            ) : (
                                <RefRow label="Solicitante">
                                    <span title="No se encontró en el padrón de usuarios retail: se muestra el identificador con el que el silo registró la solicitud.">
                                        {requester}
                                    </span>
                                </RefRow>
                            )}

                            {exception.storeId ? (
                                <RefRow label="Tienda">
                                    <Link
                                        to="/retail-admin/$section"
                                        params={{ section: 'tiendas' }}
                                        search={{ tienda: exception.storeId }}
                                        className={REF_LINK}
                                    >
                                        {storeLabel}
                                    </Link>
                                </RefRow>
                            ) : null}

                            {exception.saleId === null && exception.storeId === null ? (
                                <p className="py-4 text-center text-[11.5px] text-muted-foreground">
                                    Esta excepción no cuelga de una venta ni de una tienda.
                                </p>
                            ) : null}
                        </div>
                    </>
                ) : null}
            </div>
        </div>
    );
}
